import { createColumnHelper } from '@tanstack/react-table';
import { IMergedHeadersReturn } from './index.headers';
import { ICampaignDomainState } from '../../models/bidder-domains.model';
import { Tag } from '@monorepo/base/src/components/tag/tag';
import styles from '../../components/stats-filters/stats-filters.module.scss';
import { mapToTagStatus, Status } from '../../enums/status';
import { IRowProps } from '@monorepo/base/src/components/table/table';
import { useBidderDomainsStatesActions } from '../actions/bidder-domains-states.actions';
import { TableSelectionCell, TableSelectionHeader } from '@monorepo/base/src/components/table/table-selection-cell/table-selection-cell';
import { useRoute } from '../tools/use-route';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IAdminxStore } from '../../stores';
import { useEffect } from 'react';
import { determineButtonProperties } from '../../utils/bidder-domains-states.utils';
import { CacheDataCell } from '../../components/bidder-domains-states/cache-data-cell';

type DomainStateRowProps = ICampaignDomainState & IRowProps<ICampaignDomainState>;

const columnHelper = createColumnHelper<DomainStateRowProps>();

export const useBidderDomainsStatesHeaders = (campaignId?: number, onRefresh?: () => void): IMergedHeadersReturn<DomainStateRowProps> => {
	const { removeFromCache, removeFromTrash, isActionOngoing } = useBidderDomainsStatesActions({ campaignId, onRefresh });
	const { settingsStore } = useStores<IAdminxStore>();
	const { currentRouteWithoutSpecialChars } = useRoute();
	const tableStore = settingsStore.pages.get(currentRouteWithoutSpecialChars)?.tableStore;

	useEffect(() => {
		if (tableStore && Object.keys(tableStore.getColumnVisibility()).length === 0) {
			tableStore.setDefaultColumnVisibility({
				domain: true,
				status: true,
				paused: true,
				cache_data: true,
				action: true,
			});
			tableStore?.setColumnVisibility(tableStore.getDefaultColumnVisibility());
		}
	}, [tableStore]);

	const handleActionClick = (status: string, paused: boolean, domain: string) => {
		if (paused && status?.toLowerCase() !== 'trash') {
			removeFromCache([{ domain, status, paused }]);
			return;
		} else if (status?.toLowerCase() === 'trash') {
			removeFromTrash([{ domain, status, paused }]);
			return;
		}
	};

	return {
		columns: [
			columnHelper.display({
				id: 'selection',
				size: 40,
				enableHiding: false,
				header: headerContext => <TableSelectionHeader headerProps={headerContext} />,
				cell: cellContext => <TableSelectionCell cellProps={cellContext} />,
			}),
			columnHelper.accessor('domain', {
				header: 'Domain',
				cell: ({ row }) => <span>{row.getValue('domain')}</span>,
			}),
			columnHelper.accessor('status', {
				header: 'State',
				cell: ({ row }) => {
					const status = row.getValue('status') as string | undefined;
					if (status?.toLowerCase() !== 'trash') {
						return <Tag type={mapToTagStatus('ACTIVE' as Status)}>{status?.toLowerCase()}</Tag>;
					} else {
						return <Tag type={mapToTagStatus('ENDED' as Status)}>{status?.toLowerCase()}</Tag>;
					}
				},
			}),
			columnHelper.accessor('paused', {
				header: 'Paused in Cache',
				cell: ({ row }) => {
					const isPaused = row.getValue('paused') as boolean;
					return <span>{isPaused ? 'Yes' : 'No'}</span>;
				},
			}),
			columnHelper.accessor('cache_data', {
				header: 'Cache data',
				size: 160,
				cell: ({ row }) => {
					const isPaused = row.original.paused;
					const cacheData = row.getValue('cache_data') as string | undefined;
					if (isPaused && cacheData) {
						return <CacheDataCell cacheData={cacheData} domain={row.original.domain} />;
					}
					return <span>—</span>;
				},
			}),
			columnHelper.accessor(
				row => {
					const { buttonProperties } = determineButtonProperties(row.paused, row.status);
					return buttonProperties.actionName;
				},
				{
					id: 'action',
					header: 'Action',
					cell: ({ row }) => {
						const isPaused = row.original.paused;
						const status = row.original.status;
						const { buttonProperties } = determineButtonProperties(isPaused, status);
						if (buttonProperties.actionName === '—') {
							return <span>—</span>;
						}

						return (
							<div className={isActionOngoing ? `${styles.loadingText}` : 'links'}>
								{buttonProperties.actionName !== '—' ? (
									<a onClick={() => !isActionOngoing && handleActionClick(status, isPaused, row.original.domain)}>
										{buttonProperties.actionName}
									</a>
								) : (
									<span>-</span>
								)}
							</div>
						);
					},
				}
			),
		],
	};
};
